// Single source of truth for dashboard tabs. Sidebar, Dashboard page,
// shortcuts, the route policy, and the IAM tab all derive from this list.
//
// Every tab is a grant. A user sees a tab, and may call its routes, only when
// their session carries the tab id in `permissions` (granted from the
// Dashboard IAM tab, stored by et-auth). `audience` sets the sidebar section,
// IAM grid section and team preset. The server never reads it for access.

export interface TabDef {
  id: string;
  /** Page title (header). */
  label: string;
  /** Sidebar label when it differs from the page title. */
  navLabel?: string;
  /** Null keeps undecided/retiring tools out of team presets. */
  audience: "support" | "engineering" | "access" | null;
  shortcut: string;
}

export const TAB_REGISTRY = [
  { id: "usage", label: "Teacher Usage", audience: null, shortcut: "1" },
  { id: "conversations", label: "Conversations", navLabel: "Conversation Overview", audience: "support", shortcut: "2" },
  { id: "recordings", label: "Audio Recordings", audience: "support", shortcut: "3" },
  { id: "analytics", label: "Completion Analytics", audience: null, shortcut: "4" },
  { id: "loadtest", label: "Load Testing", audience: "engineering", shortcut: "5" },
  { id: "classroomreports", label: "Reports", audience: "support", shortcut: "r" },
  { id: "prompts", label: "Prompt Management", audience: "engineering", shortcut: "9" },
  { id: "experiments", label: "A/B Experiments", audience: null, shortcut: "0" },
  { id: "tutorversions", label: "Tutor Versions", audience: "engineering", shortcut: "v" },
  { id: "questionbank", label: "Question Bank", audience: "engineering", shortcut: "=" },
  { id: "ttsarena", label: "TTS Evaluation", audience: "engineering", shortcut: "a" },
  { id: "sttarena", label: "STT Evaluation", audience: "engineering", shortcut: "b" },
  { id: "classrooms", label: "Classrooms", audience: "support", shortcut: "6" },
  { id: "curriculum", label: "Curriculum", audience: null, shortcut: "k" },
  { id: "featureflags", label: "Feature Flags", audience: null, shortcut: "7" },
  { id: "users", label: "Users Config", audience: "support", shortcut: "8" },
  { id: "organizations", label: "Organizations", audience: "support", shortcut: "o" },
  { id: "lessonconfig", label: "Lessons", audience: null, shortcut: "-" },
  { id: "ttspool", label: "TTS Pool", audience: "engineering", shortcut: "t" },
  { id: "schedules", label: "Schedules", audience: null, shortcut: "s" },
  { id: "cleverlms", label: "Clever LMS Connect", navLabel: "Clever LMS", audience: "support", shortcut: "c" },
  { id: "datafetch", label: "Data Fetch", audience: null, shortcut: "d" },
  { id: "iam", label: "Dashboard IAM", audience: "access", shortcut: "i" },
] as const satisfies readonly TabDef[];

export type TabKey = (typeof TAB_REGISTRY)[number]["id"];

/** The tab that manages every other tab's grants. */
export const IAM_TAB: TabKey = "iam";

export const ALL_TAB_KEYS: TabKey[] = TAB_REGISTRY.map((tab) => tab.id);

export const isTabKey = (value: unknown): value is TabKey =>
  typeof value === "string" && (ALL_TAB_KEYS as string[]).includes(value);

export const getTabDef = (id: TabKey): (typeof TAB_REGISTRY)[number] => {
  const def = TAB_REGISTRY.find((tab) => tab.id === id);
  if (!def) throw new Error(`Unknown tab: ${id}`);
  return def;
};

/** The combined speech entry uses TTS when available, otherwise STT. */
export const isNavItemVisible = (grantedTabs: readonly TabKey[], tab: TabKey): boolean =>
  canViewTab(grantedTabs, tab) && !(tab === "sttarena" && grantedTabs.includes("ttsarena"));

/** Keep only known tab ids, deduped, in registry order. */
export const sanitizeTabKeys = (value: unknown): TabKey[] => {
  if (!Array.isArray(value)) return [];
  const set = new Set(value.filter(isTabKey));
  return ALL_TAB_KEYS.filter((tab) => set.has(tab));
};

export const canViewTab = (grantedTabs: readonly TabKey[], tab: TabKey): boolean =>
  grantedTabs.includes(tab);

export const TAB_AUDIENCES = [
  { id: "support", label: "Support" },
  { id: "engineering", label: "Engineering" },
  { id: null, label: "Other tools" },
  { id: "access", label: "Access" },
] as const;

/** Presets add team tabs; manual choices (including IAM) remain unchanged. */
export const addTeamPreset = (current: readonly TabKey[], audience: "support" | "engineering"): TabKey[] =>
  sanitizeTabKeys([...current, ...TAB_REGISTRY.filter((tab) => tab.audience === audience).map((tab) => tab.id)]);
